import React, { useEffect, useRef, useState } from 'react';
import { makeStyles } from '@mui/styles';
import { remCalc } from '../../../../util/style-util';
import { Box } from '@mui/material';

type Props = {
  children: JSX.Element[];
  noContentMessage?: string;
};

const scrollToBottom = (container: HTMLDivElement | null) => {
  if (container) {
    container.scrollTo(0, container.scrollHeight - container.clientHeight);
  }
};

// TODO: handle case where user manually scrolls up and a new message is added
export const ScrollToBottom = ({ children, noContentMessage = '' }: Props): JSX.Element => {
  const [hasManuallyScrolled, setHasManuallyScrolled] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const scrollToBottomTimeout = useRef<any>();
  const classes = useStyles();

  useEffect(() => {
    if (containerRef.current) {
      const options: IntersectionObserverInit = {
        root: containerRef.current,
        rootMargin: '0px',
        threshold: 1.0
      };

      const callback = entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting && !hasManuallyScrolled) {
            scrollToBottomTimeout.current = setTimeout(
              () => scrollToBottom(containerRef.current),
              200
            );
          } else if (entry.isIntersecting) {
            setHasManuallyScrolled(false);
          }
        });
      };

      const observer: IntersectionObserver = new IntersectionObserver(callback, options);

      if (endRef.current) observer.observe(endRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleManualScroll = () => {
    setHasManuallyScrolled(true);
    if (scrollToBottomTimeout.current) {
      clearTimeout(scrollToBottomTimeout.current);
    }
  };

  return (
    <div
      className={classes.scrollToBottomContainer}
      ref={containerRef}
      onScroll={handleManualScroll}
    >
      {children && children.length ? (
        children
      ) : (
        <Box key={0} className={classes.noContentMessageBox}>
          <Box>{noContentMessage}</Box>
        </Box>
      )}

      <div className={classes.scrollToBottomEnd} ref={endRef} />
    </div>
  );
};

const useStyles = makeStyles(() => {
  const paddingTop = 20;
  const paddingLeft = 24;
  const paddingRight = 24;
  return {
    scrollToBottomContainer: {
      height: '100%',
      width: '95%',
      overflow: 'auto',
      paddingTop: remCalc(paddingTop),
      paddingLeft: remCalc(paddingLeft),
      paddingRight: remCalc(paddingRight)
    },
    noContentMessageBox: {
      width: `calc(100% - ${remCalc(paddingLeft + paddingRight)})`,
      height: `calc(100% - ${remCalc(paddingTop)})`,
      display: 'flex',
      alignContent: 'center',
      alignItems: 'center',
      justifyContent: 'center'
    },
    scrollToBottomEnd: {
      height: 5,
      width: '100%'
    }
  };
});
